/* eslint-disable react/no-unescaped-entities, react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Send, Package, Gavel, ChevronDown } from "lucide-react";
import { formatIDR } from "../lib/format";

export default function Chat() {
  const { user, loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTo = searchParams.get("to");
  const initialProduct = searchParams.get("product");
  const initialAuction = searchParams.get("auction");
  const [conversations, setConversations] = useState([]);
  const [activeOther, setActiveOther] = useState(initialTo || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [contextItem, setContextItem] = useState(null); // { kind, id, title, image, price, status }
  const [orderListOpen, setOrderListOpen] = useState(false);
  const [orderList, setOrderList] = useState([]);
  const scrollRef = useRef(null);

  const loadConvs = async () => {
    const { data } = await api.get("/chat/conversations");
    setConversations(data);
    if (!activeOther && data.length > 0) setActiveOther(data[0].other_user_id);
  };

  const loadMessages = async () => {
    if (!activeOther) return;
    const { data } = await api.get(`/chat/messages/${activeOther}`);
    setMessages(data);
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 100);
  };

  useEffect(() => {
    if (!user || user === false) return;
    let alive = true;
    api.get("/chat/conversations").then(({ data }) => {
      if (!alive) return;
      setConversations(data);
      if (!activeOther && data.length > 0) setActiveOther(data[0].other_user_id);
    }).catch(() => {});
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    if (!activeOther) return;
    let alive = true;
    api.get(`/chat/messages/${activeOther}`).then(({ data }) => {
      if (!alive) return;
      setMessages(data);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 100);
    }).catch(() => {});
    return () => { alive = false; };
  }, [activeOther]);

  useEffect(() => {
    if (!activeOther) return;
    const t = setInterval(loadMessages, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [activeOther]);

  // Load context card (product or auction) from URL params
  useEffect(() => {
    const load = async () => {
      try {
        if (initialAuction) {
          const { data } = await api.get(`/auctions/${initialAuction}`);
          setContextItem({ kind: "auction", id: data.id, title: data.title, image: data.image_url, price: data.winner_bid_amount || data.current_bid_amount || data.starting_bid, status: data.status, link: `/auctions/${data.id}`, payout_done: data.payout_done });
        } else if (initialProduct) {
          const { data } = await api.get(`/products/${initialProduct}`);
          setContextItem({ kind: "product", id: data.id, title: data.title, image: data.image_url, price: data.price, status: data.status, link: `/product/${data.id}` });
        } else {
          setContextItem(null);
        }
      } catch { /* noop */ }
    };
    load();
  }, [initialProduct, initialAuction]);

  // Order list (auctions/orders involving the other user)
  useEffect(() => {
    const fetchOrders = async () => {
      if (!activeOther || !orderListOpen) return;
      try {
        // Reuse auctions involving this user pair (won by either side)
        const { data: bids } = await api.get("/auctions/my-bids");
        const items = (bids || [])
          .filter((b) => b.auction.seller_id === activeOther || b.auction.winner_user_id === activeOther)
          .map((b) => ({ kind: "auction", id: b.auction.id, title: b.auction.title, image: b.auction.image_url, price: b.auction.winner_bid_amount || b.auction.current_bid_amount || b.auction.starting_bid, status: b.auction.status, link: `/auctions/${b.auction.id}` }));
        setOrderList(items);
      } catch { /* noop */ }
    };
    fetchOrders();
  }, [activeOther, orderListOpen]);

  if (loading) return <div className="text-white/50 text-center py-32">Loading...</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeOther) return;
    await api.post("/chat/send", { recipient_id: activeOther, message: text, product_id: initialProduct });
    setText("");
    loadMessages();
    loadConvs();
  };

  return (
    <div data-testid="chat-page" className="max-w-6xl mx-auto px-4 md:px-8 py-12">
      <span className="tag tag-accent">// CONVERSATIONS</span>
      <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mt-3 mb-8">Live Chat</h1>

      <div className="grid md:grid-cols-[280px_1fr] gap-4 border border-white/10 min-h-[60vh]">
        <aside className="border-r border-white/10 overflow-y-auto">
          {conversations.length === 0 && !activeOther ? (
            <p className="text-white/50 text-sm p-6">No conversations yet.</p>
          ) : (
            conversations.map((c) => (
              <button
                key={c.conversation_id}
                onClick={() => setActiveOther(c.other_user_id)}
                data-testid={`conv-${c.other_user_id}`}
                className={`w-full text-left p-4 border-b border-white/10 hover:bg-white/5 ${activeOther === c.other_user_id ? "bg-[#00F0FF]/10" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-white text-sm truncate">{c.other_user_name}</div>
                  {c.unread_count > 0 && activeOther !== c.other_user_id && (
                    <span data-testid={`conv-unread-${c.other_user_id}`} className="min-w-[18px] h-[18px] px-1 bg-[#FF003C] text-white text-[10px] font-bold flex items-center justify-center rounded-full flex-shrink-0">
                      {c.unread_count > 99 ? "99+" : c.unread_count}
                    </span>
                  )}
                </div>
                <div className="text-xs text-white/50 truncate mt-1">{c.last_message}</div>
              </button>
            ))
          )}
          {activeOther && !conversations.find((c) => c.other_user_id === activeOther) && (
            <div className="p-4 border-b border-white/10 bg-[#00F0FF]/10">
              <div className="font-bold text-white text-sm">New chat</div>
              <div className="text-xs text-white/50 truncate mt-1">Start a conversation</div>
            </div>
          )}
        </aside>

        <div className="flex flex-col">
          {/* Context card: product/auction yang sedang dibicarakan */}
          {activeOther && (
            <div className="border-b border-white/10 bg-black/40 p-3 flex items-center gap-3 relative" data-testid="chat-context-card">
              {contextItem ? (
                <>
                  {contextItem.image && (
                    <img src={contextItem.image} alt="" className="w-14 h-14 object-cover border border-white/10" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {contextItem.kind === "auction" ? <Gavel className="w-3 h-3 text-[#7C6EFF]" /> : <Package className="w-3 h-3 text-[#00F0FF]" />}
                      <span className="text-[10px] uppercase tracking-widest text-white/40">{contextItem.kind === "auction" ? "Auction" : "Produk"} · {contextItem.status?.toUpperCase()}</span>
                    </div>
                    <p className="font-bold text-white text-sm truncate">{contextItem.title}</p>
                    <p className="text-[#00F0FF] text-xs font-bold">{formatIDR(contextItem.price || 0)}</p>
                  </div>
                  <Link to={contextItem.link} className="text-xs uppercase tracking-widest text-[#00F0FF] hover:underline whitespace-nowrap">Lihat →</Link>
                </>
              ) : (
                <div className="flex-1 text-xs text-white/40 italic">Tidak ada produk/auction terkait. Tap "Daftar Pesanan" untuk pilih.</div>
              )}
              <button
                data-testid="order-list-toggle"
                onClick={() => setOrderListOpen((v) => !v)}
                className="inline-flex items-center gap-1 px-3 py-2 border border-white/15 text-white/70 hover:border-[#00F0FF] hover:text-[#00F0FF] text-[10px] uppercase font-bold tracking-widest"
              >
                Daftar Pesanan <ChevronDown className={`w-3 h-3 transition-transform ${orderListOpen ? "rotate-180" : ""}`} />
              </button>
              {orderListOpen && (
                <div className="absolute right-3 top-full mt-1 w-72 max-h-80 overflow-y-auto bg-[#0a0a0a] border border-white/15 z-20" data-testid="order-list-popup">
                  {orderList.length === 0 ? (
                    <p className="text-white/40 text-xs p-3">Tidak ada pesanan terkait.</p>
                  ) : (
                    orderList.map((it) => (
                      <button
                        key={it.id}
                        data-testid={`order-item-${it.id}`}
                        onClick={() => {
                          setContextItem(it);
                          setSearchParams({ to: activeOther, ...(it.kind === "auction" ? { auction: it.id } : { product: it.id }) });
                          setOrderListOpen(false);
                        }}
                        className="w-full text-left p-3 border-b border-white/10 hover:bg-white/5 flex items-center gap-2"
                      >
                        {it.image && <img src={it.image} alt="" className="w-10 h-10 object-cover border border-white/10" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-bold truncate">{it.title}</p>
                          <p className="text-[#00F0FF] text-[10px]">{formatIDR(it.price || 0)} · {it.status}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[55vh]" data-testid="chat-messages">
            {!activeOther ? (
              <p className="text-white/40 text-center pt-12">Select a conversation</p>
            ) : messages.length === 0 ? (
              <p className="text-white/40 text-center pt-12">No messages yet. Say hi 👋</p>
            ) : (
              messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender_id === user.id ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`msg-${m.id}`}
                    className={`max-w-[70%] p-3 text-sm ${
                      m.sender_id === user.id
                        ? "bg-[#00F0FF] text-black"
                        : "bg-white/5 text-white border border-white/10"
                    }`}
                  >
                    {m.message}
                  </div>
                </div>
              ))
            )}
          </div>
          {activeOther && (
            <form onSubmit={send} className="border-t border-white/10 p-3 flex gap-2" data-testid="chat-form">
              <input
                data-testid="chat-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 bg-black border border-white/15 focus:border-[#00F0FF] focus:outline-none text-white p-3 text-sm"
              />
              <button data-testid="chat-send-btn" type="submit" className="btn-primary inline-flex items-center gap-2">
                <Send className="w-4 h-4" /> Send
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
